// ui/pinnedQuestsUI.js
// Pinned quests panel - displays tracked quest requirements in sidebar

import { gameState } from '../game/gameState.js';
import { getQuest, QUEST_GIVERS } from '../data/quests/questData.js';
import { getItem, getItemIcon } from '../data/items/index.js';
import { formatNumber } from '../utils/format.js';
import { skillRegistry } from '../core/skillRegistry.js';
import { showQuestModal } from './questModal.js';

/**
 * Render the pinned quests panel
 */
export function renderPinnedQuests() {
    const container = document.getElementById('pinned-quests-container');
    if (!container) return;

    const pinnedQuestNumbers = gameState.getPinnedQuests();

    const sidebar = document.getElementById('info-sidebar');

    // Hide container if no pinned quests
    if (pinnedQuestNumbers.length === 0) {
        container.innerHTML = '';
        container.style.display = 'none';
        if (sidebar) sidebar.classList.remove('has-pinned-quests');
        return;
    }

    container.style.display = 'block';
    if (sidebar) sidebar.classList.add('has-pinned-quests');

    let html = '<div class="pinned-quests-panel">';

    for (const questNumber of pinnedQuestNumbers) {
        const quest = getQuest(questNumber);
        if (!quest) continue;

        const giver = QUEST_GIVERS[Object.keys(QUEST_GIVERS).find(k => QUEST_GIVERS[k].id === quest.giver)];

        html += `
            <div class="pinned-quest-card" data-quest="${questNumber}">
                <div class="pinned-quest-header">
                    <span class="pinned-quest-giver">${giver?.icon || '?'}</span>
                    <span class="pinned-quest-name">${quest.name}</span>
                    <button class="pinned-quest-unpin" onclick="event.stopPropagation(); window.pinnedQuestsUI.unpin(${questNumber})">×</button>
                </div>
                <div class="pinned-quest-requirements">
                    ${renderPinnedRequirements(quest)}
                </div>
                <button class="pinned-quest-view btn btn-small" onclick="event.stopPropagation(); window.pinnedQuestsUI.viewQuest(${questNumber})">
                    View Quest
                </button>
            </div>
        `;
    }

    html += '</div>';
    container.innerHTML = html;
}

/**
 * Render requirements for a pinned quest (compact version)
 */
function renderPinnedRequirements(quest) {
    const req = quest.requirements;
    if (!req || Object.keys(req).length === 0) {
        return '<div class="pinned-req-row met">No requirements</div>';
    }

    let html = '';

    // Skill requirements
    if (req.skills) {
        for (const [skillId, level] of Object.entries(req.skills)) {
            const skillData = skillRegistry.get(skillId);
            const playerLevel = gameState.getSkill(skillId)?.level || 0;
            const met = playerLevel >= level;
            html += `
                <div class="pinned-req-row ${met ? 'met' : 'unmet'}">
                    <span class="pinned-req-icon">${skillRegistry.getSkillIcon(skillId)}</span>
                    <span class="pinned-req-text">${skillData?.name || skillId} Lv${level}</span>
                    <span class="pinned-req-status">${met ? '✓' : `${playerLevel}/${level}`}</span>
                </div>
            `;
        }
    }

    // Gold requirement
    if (req.gold) {
        const playerGold = gameState.getGold();
        const met = playerGold >= req.gold;
        html += `
            <div class="pinned-req-row ${met ? 'met' : 'unmet'}">
                <span class="pinned-req-icon">${getItemIcon('coins')}</span>
                <span class="pinned-req-text">${formatNumber(req.gold)} Gold</span>
                <span class="pinned-req-status">${met ? '✓' : `${formatNumber(playerGold)}/${formatNumber(req.gold)}`}</span>
            </div>
        `;
    }

    // Item requirements
    if (req.items) {
        for (const [itemId, qty] of Object.entries(req.items)) {
            const item = getItem(itemId);
            const playerQty = gameState.getItemCount(itemId);
            const bankQty = gameState.getBankItemCount(itemId);
            const met = playerQty >= qty;
            const canMeetWithBank = !met && (playerQty + bankQty) >= qty;

            let statusClass = 'met';
            if (!met) {
                statusClass = canMeetWithBank ? 'can-meet-with-bank' : 'unmet';
            }

            html += `
                <div class="pinned-req-row ${statusClass}">
                    <span class="pinned-req-icon">${getItemIcon(itemId)}</span>
                    <span class="pinned-req-text">${item?.name || itemId}</span>
                    <span class="pinned-req-status">${met ? '✓' : `${playerQty}/${qty}`}</span>
                    ${bankQty > 0 && !met ? `<span class="pinned-bank-indicator">(+${bankQty} bank)</span>` : ''}
                </div>
            `;
        }
    }

    // Quest requirements
    if (req.quests) {
        for (const prereqNum of req.quests) {
            const prereq = getQuest(prereqNum);
            const met = gameState.isQuestCompleted(prereqNum);
            html += `
                <div class="pinned-req-row ${met ? 'met' : 'unmet'}">
                    <span class="pinned-req-icon">📜</span>
                    <span class="pinned-req-text">${prereq?.name || `Quest #${prereqNum}`}</span>
                    <span class="pinned-req-status">${met ? '✓' : '✗'}</span>
                </div>
            `;
        }
    }

    return html || '<div class="pinned-req-row met">No requirements</div>';
}

/**
 * Update pinned quests counts (called every tick from inventory update)
 * Only updates the count text, doesn't re-render the whole panel
 */
export function updatePinnedQuests() {
    const container = document.getElementById('pinned-quests-container');
    if (!container || container.style.display === 'none') return;

    const pinnedQuestNumbers = gameState.getPinnedQuests();
    if (pinnedQuestNumbers.length === 0) return;

    // Update each pinned quest's requirement counts
    for (const questNumber of pinnedQuestNumbers) {
        const card = container.querySelector(`.pinned-quest-card[data-quest="${questNumber}"]`);
        if (!card) continue;

        const quest = getQuest(questNumber);
        if (!quest) continue;

        // Update requirement rows
        const reqRows = card.querySelectorAll('.pinned-req-row');
        updateRequirementRows(quest, reqRows);
    }
}

/**
 * Update requirement row counts without re-rendering
 */
function updateRequirementRows(quest, reqRows) {
    const req = quest.requirements;
    if (!req) return;

    let rowIndex = 0;

    // Update skill requirements
    if (req.skills) {
        for (const [skillId, level] of Object.entries(req.skills)) {
            const row = reqRows[rowIndex];
            if (row) {
                const playerLevel = gameState.getSkill(skillId)?.level || 0;
                const met = playerLevel >= level;
                row.className = `pinned-req-row ${met ? 'met' : 'unmet'}`;
                const statusEl = row.querySelector('.pinned-req-status');
                if (statusEl) statusEl.textContent = met ? '✓' : `${playerLevel}/${level}`;
            }
            rowIndex++;
        }
    }

    // Update gold requirement
    if (req.gold) {
        const row = reqRows[rowIndex];
        if (row) {
            const playerGold = gameState.getGold();
            const met = playerGold >= req.gold;
            row.className = `pinned-req-row ${met ? 'met' : 'unmet'}`;
            const statusEl = row.querySelector('.pinned-req-status');
            if (statusEl) statusEl.textContent = met ? '✓' : `${formatNumber(playerGold)}/${formatNumber(req.gold)}`;
        }
        rowIndex++;
    }

    // Update item requirements
    if (req.items) {
        for (const [itemId, qty] of Object.entries(req.items)) {
            const row = reqRows[rowIndex];
            if (row) {
                const playerQty = gameState.getItemCount(itemId);
                const bankQty = gameState.getBankItemCount(itemId);
                const met = playerQty >= qty;
                const canMeetWithBank = !met && (playerQty + bankQty) >= qty;

                let statusClass = 'met';
                if (!met) {
                    statusClass = canMeetWithBank ? 'can-meet-with-bank' : 'unmet';
                }
                row.className = `pinned-req-row ${statusClass}`;

                const statusEl = row.querySelector('.pinned-req-status');
                if (statusEl) statusEl.textContent = met ? '✓' : `${playerQty}/${qty}`;

                const bankEl = row.querySelector('.pinned-bank-indicator');
                if (bankEl) {
                    bankEl.textContent = (bankQty > 0 && !met) ? `(+${bankQty} bank)` : '';
                }
            }
            rowIndex++;
        }
    }

    // Update quest requirements
    if (req.quests) {
        for (const prereqNum of req.quests) {
            const row = reqRows[rowIndex];
            if (row) {
                const met = gameState.isQuestCompleted(prereqNum);
                row.className = `pinned-req-row ${met ? 'met' : 'unmet'}`;
                const statusEl = row.querySelector('.pinned-req-status');
                if (statusEl) statusEl.textContent = met ? '✓' : '✗';
            }
            rowIndex++;
        }
    }
}

/**
 * Unpin a quest
 */
export function unpinQuest(questNumber) {
    gameState.unpinQuest(questNumber);
    renderPinnedQuests();
}

/**
 * View quest in modal
 */
export function viewQuest(questNumber) {
    // Import dynamically to avoid circular dependency
    import('./questModal.js').then(({ showQuestModal, selectQuest }) => {
        showQuestModal();
        // Small delay to ensure modal is rendered before selecting
        setTimeout(() => {
            if (window.questModal?.selectQuest) {
                window.questModal.selectQuest(questNumber);
            }
        }, 50);
    });
}

/**
 * Initialize pinned quests UI
 */
export function initPinnedQuestsUI() {
    renderPinnedQuests();
}

// Expose to window for onclick handlers
window.pinnedQuestsUI = {
    render: renderPinnedQuests,
    update: updatePinnedQuests,
    unpin: unpinQuest,
    viewQuest
};

export default {
    renderPinnedQuests,
    updatePinnedQuests,
    initPinnedQuestsUI
};
